/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Pencil,
  Trash2,
  GripVertical,
  Search,
  Check,
  X as XIcon,
  Plus,
} from "lucide-react";
import { directoryApi, formatPhone } from "./api";
import "./AdminDirectoryPage.css";

const TABS = [
  { id: "contacts", label: "All Contacts" },
  { id: "categories", label: "Categories" },
];

export default function AdminDirectoryPage() {
  const [tab, setTab] = useState("contacts");
  return (
    <div className="vsd-adm-shell">
      <div className="vsd-adm-head">
        <div>
          <h1>Directory Management</h1>
          <p>Edit contacts, manage categories.</p>
        </div>
      </div>
      <div className="vsd-adm-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={t.id === tab ? "active" : ""}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "contacts" && <AdminContactsTab />}
      {tab === "categories" && <AdminCategoriesTab />}
    </div>
  );
}

function AdminContactsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await directoryApi.adminListContacts({
        q: debouncedQ || undefined,
        page,
        pageSize: 50,
      });
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, page]);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    setDeletingId(item.id);
    try {
      await directoryApi.deleteContact(item.id);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="vsd-adm-controls">
        <div className="vsd-adm-search">
          <Search size={15} />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or phone…"
          />
        </div>
        <div className="vsd-adm-total">{total} total</div>
      </div>

      {error && <div className="vsd-error">{error}</div>}
      {loading && items.length === 0 && (
        <div className="vsd-empty">Loading…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="vsd-empty">
          {debouncedQ ? "No contacts match." : "No contacts yet."}
        </div>
      )}

      {items.length > 0 && (
        <div className="vsd-adm-table-wrap">
          <table className="vsd-adm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Phone</th>
                <th>Rating</th>
                <th>Reviews</th>
                <th>Added by</th>
                <th className="vsd-adm-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/directory/contact/${c.id}`}>{c.name}</Link>
                    {c.is_community_pick && (
                      <span className="vsd-adm-pick">Pick</span>
                    )}
                  </td>
                  <td>{c.category?.name || "—"}</td>
                  <td className="vsd-adm-phone">
                    {formatPhone(c.primary_phone)}
                  </td>
                  <td>{c.rating > 0 ? c.rating.toFixed(1) : "—"}</td>
                  <td>{c.review_count}</td>
                  <td>{c.created_by?.name || "—"}</td>
                  <td className="vsd-adm-actions-col">
                    <button
                      className="vsd-icon-btn"
                      onClick={() => navigate(`/directory/contact/${c.id}/edit`)}
                      aria-label="Edit"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className="vsd-icon-btn danger"
                      onClick={() => onDelete(c)}
                      disabled={deletingId === c.id}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function AdminCategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [draggingId, setDraggingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setCats(await directoryApi.listCategories());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onAdd = async (e) => {
    e?.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await directoryApi.createCategory(newName.trim());
      setNewName("");
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const onSaveRename = async (cat) => {
    try {
      await directoryApi.patchCategory(cat.id, { name: editName.trim() });
      setEditingId(null);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const onDelete = async (cat) => {
    if (
      !window.confirm(
        `Delete "${cat.name}"? Contacts in this category must be moved first.`
      )
    )
      return;
    try {
      await directoryApi.deleteCategory(cat.id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const reorder = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...cats];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setCats(next);
    // Re-stamp display_order on all rows. Cheap at <30 categories.
    try {
      await Promise.all(
        next.map((c, i) =>
          directoryApi.patchCategory(c.id, { display_order: (i + 1) * 10 })
        )
      );
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="vsd-adm-cats">
      <form className="vsd-adm-cat-add" onSubmit={onAdd}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category name"
          maxLength={64}
        />
        <button
          type="submit"
          className="vsd-btn-primary"
          disabled={adding || !newName.trim()}
        >
          <Plus size={14} /> Add
        </button>
      </form>

      {error && <div className="vsd-error">{error}</div>}
      {loading && <div className="vsd-empty">Loading…</div>}

      <div className="vsd-adm-cat-list">
        {cats.map((c, idx) => (
          <div
            key={c.id}
            className={`vsd-adm-cat-row ${draggingId === c.id ? "dragging" : ""}`}
            draggable
            onDragStart={() => setDraggingId(c.id)}
            onDragEnd={() => setDraggingId(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (!draggingId || draggingId === c.id) return;
              const from = cats.findIndex((x) => x.id === draggingId);
              if (from === -1) return;
              reorder(from, idx);
            }}
          >
            <span className="vsd-adm-grip" aria-hidden>
              <GripVertical size={14} />
            </span>
            {editingId === c.id ? (
              <>
                <input
                  className="vsd-adm-cat-rename"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                />
                <button
                  className="vsd-icon-btn"
                  onClick={() => onSaveRename(c)}
                  aria-label="Save"
                >
                  <Check size={14} />
                </button>
                <button
                  className="vsd-icon-btn"
                  onClick={() => setEditingId(null)}
                  aria-label="Cancel"
                >
                  <XIcon size={14} />
                </button>
              </>
            ) : (
              <>
                <div className="vsd-adm-cat-name">
                  {c.name}
                  {c.is_seed && <span className="vsd-adm-seed">seed</span>}
                </div>
                <div className="vsd-adm-cat-count">
                  {c.contact_count}{" "}
                  {c.contact_count === 1 ? "contact" : "contacts"}
                </div>
                <button
                  className="vsd-icon-btn"
                  onClick={() => {
                    setEditingId(c.id);
                    setEditName(c.name);
                  }}
                  aria-label="Rename"
                  title="Rename"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="vsd-icon-btn danger"
                  onClick={() => onDelete(c)}
                  disabled={c.contact_count > 0}
                  aria-label="Delete"
                  title={
                    c.contact_count > 0
                      ? "Move contacts first"
                      : "Delete"
                  }
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
